import { z } from 'zod';
import { ohneSteuerzeichen } from './steuerzeichen';
import { HONEYPOT_FIELD_NAME, honeypotFieldSchema } from './honeypot';
import { SLUG_RE } from './vermarktung-feed';
/**
 * DE OBJEKT-ANFRAGE (order E1, 17-10): «Besichtigung anfragen» en «Unterlagen
 * anfragen» op de Kauf-Objektseite — ÉÉN aanvraagsoort met `anliegen ∈
 * {besichtigung, unterlagen}`, een eigen tabel (`objekt_anfragen`) en een
 * bevestigingsmail mét een kopie van de ingevulde gegevens (E12).
 *
 * DE KETEN: widget `/immobilienpreise/objekt-anfrage/` → `POST /api/objekt-anfrage`
 * (edge: zod, honeypot, `listingGates`, HMAC) → `POST /ingest/objekt-anfrage`
 * (backend: Turnstile, eigen dagcap, poort, dedupe, één transactie) → event
 * `objekt_anfrage.requested` (PII-vrij) → router → E12 + teammail, beide uit het
 * register gerenderd UIT DE DATABASE (B6-vorm), nooit uit event-parameters.
 *
 * WAAROM ÉÉN BESTAND IN BEIDE BARRELS (de Vor-Ort-vorm, niet de Exposé-splitsing):
 * de woorden (`anliegen` + labels), het antwoord van de leadkaart en de
 * objectlijst leest het CRM als WAARDE; de draad lezen edge en backend. Alles is
 * zod plus twee client-veilige constanten (`SLUG_RE`, de honeypot-naam), dus het
 * bestand trekt niets in een browserbundel wat daar niet al zat.
 *
 * WAT ER NOOIT OP DE DRAAD STAAT (order §1.2/§1.3): de feed-`id`, `transaktion`,
 * `utm_*`, een titel of een adres, een `tenant`. Het object en zijn firma volgen
 * uit de `slug` (globaal uniek, feed-contract §1); de titel en het adres zet de
 * backend in de mails, uit de database — er is dus geen injecteerbare tekst via
 * de iframe-URL. `.strict()` maakt elk onbekend veld een 400 `invalid-input`.
 */
/** De twee anliegen (order §1.1). De VOLGORDE is die van het CRM-filter en de chip. */
export const OBJEKT_ANFRAGE_ANLIEGEN = ['besichtigung', 'unterlagen'];
/** Het ENE woord per anliegen — CRM-chip, takentitel en teammail lezen deze lijst. */
export const OBJEKT_ANFRAGE_ANLIEGEN_LABELS = {
    besichtigung: 'Besichtigung',
    unterlagen: 'Unterlagen',
};
/**
 * revDSG-versie van de toestemmingstekst die het widget toont («Ich bin
 * einverstanden, dass die MLG Immobilien AG meine Angaben zur Bearbeitung dieser
 * Anfrage verwendet. …»). LETTERLIJK gepind op de draad (order §1.2): een andere
 * waarde is een 400, zodat de consent-rij nooit een versie draagt die geen
 * bestaande tekst noemt. Een nieuwe tekst = een nieuwe versie hier, backend en
 * edge eerst, de pagina daarna.
 */
export const OBJEKT_ANFRAGE_PRIVACY_VERSION = 'objekt-anfrage-privacy-2026.1';
/** `vorname`/`nachname`: de lengte van `nameSchema` (crm.ts) en van Vor-Ort. */
export const OBJEKT_ANFRAGE_NAME_MAX = 100;
/** `telefon`: vrij gespeld, kort genoeg om nooit een payloadkanaal te zijn. */
export const OBJEKT_ANFRAGE_TELEFON_MAX = 40;
/** `nachricht`: de `maxlength` van het ontwerp (objekt.js, D4b). */
export const OBJEKT_ANFRAGE_NACHRICHT_MAX = 2000;
/**
 * Het telefoonnummer is OPTIONEEL en vrij gespeld (order §1.2): alleen cijfers,
 * spaties en `+ ( ) / . -`. Bewust GEEN E.164-dwang zoals Vor-Ort: het veld is
 * «für die Terminvereinbarung», niet een terugbelverplichting, en een
 * buitenlands nummer mag er gewoon staan. Leeg (na trim) = weggelaten.
 */
export const OBJEKT_ANFRAGE_TELEFON_RE = /^[0-9 +()/.-]*$/;
// De stuurteken-wal van de draad (#678 R1 AW-4) woont sinds micro-B B1 in
// `steuerzeichen.ts` — één waarheid voor Objekt-Anfrage en Vor-Ort.
const zonderSteuerzeichen = (v) => ohneSteuerzeichen(v);
/** De vrije tekst: 1–2000 na trim, regeleinden (LF) ja, elk ander stuurteken nee. */
export const objektAnfrageNachrichtSchema = z
    .string()
    .refine((v) => ohneSteuerzeichen(v, true), 'steuerzeichen')
    .trim()
    .min(1)
    .max(OBJEKT_ANFRAGE_NACHRICHT_MAX);
/**
 * `telefon`: getrimd, ≤ 40, het toegestane alfabet — en een LEGE waarde is
 * hetzelfde als geen waarde (order §1.2 «leer = weggelassen»), zodat er nooit
 * een lege string in `objekt_anfragen.telefon` of `leads.phone` belandt.
 */
export const objektAnfrageTelefonSchema = z
    .string()
    .refine(zonderSteuerzeichen, 'steuerzeichen')
    .trim()
    .max(OBJEKT_ANFRAGE_TELEFON_MAX)
    .regex(OBJEKT_ANFRAGE_TELEFON_RE)
    .optional()
    .transform((v) => (v === undefined || v === '' ? undefined : v));
/**
 * `POST /api/objekt-anfrage` → `POST /ingest/objekt-anfrage`. STRICT: een
 * onbekende sleutel (`id`, `transaktion`, `utm_source`, `titel`, `tenant`, …)
 * is een 400 — dat is de wal die order §1.2 vraagt, en hij geldt aan beide
 * kanten omdat edge en backend hetzelfde schema parsen.
 */
export const objektAnfrageIngestSchema = z
    .object({
    /** De publieke sleutel van het object — dezelfde vorm als de Exposé-Anfrage. */
    slug: z
        .string()
        .refine(zonderSteuerzeichen, 'steuerzeichen')
        .trim()
        .toLowerCase()
        .min(1)
        .max(160)
        .regex(SLUG_RE),
    anliegen: z.enum(OBJEKT_ANFRAGE_ANLIEGEN),
    vorname: z
        .string()
        .refine(zonderSteuerzeichen, 'steuerzeichen')
        .trim()
        .min(1)
        .max(OBJEKT_ANFRAGE_NAME_MAX),
    nachname: z
        .string()
        .refine(zonderSteuerzeichen, 'steuerzeichen')
        .trim()
        .min(1)
        .max(OBJEKT_ANFRAGE_NAME_MAX),
    email: z
        .string()
        .refine(zonderSteuerzeichen, 'steuerzeichen')
        .trim()
        .toLowerCase()
        .email()
        .max(254),
    telefon: objektAnfrageTelefonSchema,
    nachricht: objektAnfrageNachrichtSchema,
    consentVersion: z.literal(OBJEKT_ANFRAGE_PRIVACY_VERSION),
    /** Cloudflare Turnstile, action `objekt_anfrage` — de backend verifieert (single-use). */
    turnstileToken: z.string().refine(zonderSteuerzeichen, 'steuerzeichen').min(1).max(4096),
    /** Honeypot — dezelfde naam als elk publiek formulier; alleen aanwezig als gevuld. */
    [HONEYPOT_FIELD_NAME]: honeypotFieldSchema,
})
    .strict();
/**
 * Het 202-orakel (order §1.4, de Exposé-vorm): onbekende slug, inactief,
 * verkocht of mandaatloos object, en een duplicaat binnen 24 uur antwoorden
 * EXACT als een aangenomen aanvraag. `.strict()` is de wal tegen een veld dat
 * «waarom» zou verklappen.
 */
export const objektAnfrageReplySchema = z.object({ ok: z.literal(true) }).strict();
// ─────────────── CRM: de leadkaart «Objekt-Anfrage» ───────────────
/**
 * `GET /leads/:id/objekt-anfrage` — de kaart naast «Vor-Ort-Termin». `anfrage`
 * is de ENE objekt-anfrage achter de lead (`objekt_anfragen.leadId` is uniek:
 * elke aanvraag maakt haar eigen lead), of `null` voor elke andere lead.
 *
 * `anliegen` als `z.string()` en niet als enum: het CRM vertaalt met de
 * labelkaart en toont een onbekend woord als zichzelf, zodat een waarde die de
 * backend eerst leert de kaart nooit breekt (de B7/Vor-Ort-vorm).
 * `bestaetigungGesendet` = `bestaetigungAt` gezet (de E12-render is uitgelost).
 */
export const leadObjektAnfrageSchema = z
    .object({
    id: z.string(),
    anliegen: z.string(),
    objektId: z.string(),
    objektTitel: z.string().nullable(),
    objektSlug: z.string(),
    telefon: z.string().nullable(),
    nachricht: z.string(),
    taskId: z.string().nullable(),
    bestaetigungAt: z.string().datetime().nullable(),
    createdAt: z.string().datetime(),
})
    .strict();
export const leadObjektAnfrageAntwortSchema = z
    .object({ anfrage: leadObjektAnfrageSchema.nullable() })
    .strict();
// ─────────────── CRM: de objectlijst «Besichtigungen und Unterlagen» ───────────────
/**
 * Taakstatus in de lijst: open (`open`/`in_progress`), af (`done`), afgebroken
 * (`cancelled`) — of er is geen taak (testaanvraag, verwijderde taak).
 */
export const OBJEKT_ANFRAGE_TASK_STATUS = ['offen', 'erledigt', 'abgebrochen', 'keine'];
/**
 * Eén rij van `GET /vermarktung/objekte/:id/objekt-anfragen`. Name, e-mail en
 * telefoon komen uit de AANVRAAG (wat de persoon in dit formulier typte), niet
 * uit de lead — die kan intussen bewerkt zijn. Nooit `ipHash`, nooit de
 * nachricht (die staat op de leadkaart, achter één klik).
 */
export const objektAnfrageZeileSchema = z
    .object({
    id: z.string().uuid(),
    leadId: z.string().uuid(),
    name: z.string().max(240),
    email: z.string().email().max(254),
    telefon: z.string().max(OBJEKT_ANFRAGE_TELEFON_MAX).nullable(),
    anliegen: z.string(),
    angefragtAt: z.string().datetime(),
    taskStatus: z.enum(OBJEKT_ANFRAGE_TASK_STATUS),
})
    .strict();
/** Dezelfde harde bovengrens als de Exposé-lijst (200), mét het echte totaal ernaast. */
export const OBJEKT_ANFRAGEN_MAX = 200;
export const objektAnfragenListeSchema = z
    .object({
    items: z.array(objektAnfrageZeileSchema).max(OBJEKT_ANFRAGEN_MAX),
    totaal: z.number().int().nonnegative(),
})
    .strict();
